/**
 * MusicPlayer 设置持久化（localStorage + cookie）
 * 抽离自 MusicPlayer.js，无 React 依赖。
 */

export const FADE_IN_OUT_COOKIE = 'music_player_fade_in_out';

export function getCookieValue(name) {
  try {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [key, value] = cookie.trim().split('=');
      if (key === name) return value;
    }
  } catch (_) {}
  return null;
}

export function setCookieValue(name, value, maxAgeSeconds = 31536000) {
  try {
    document.cookie = `${name}=${value}; path=/; max-age=${maxAgeSeconds}`;
  } catch (_) {}
}

export function getFadeInOutFromCookie() {
  const raw = getCookieValue(FADE_IN_OUT_COOKIE);
  if (raw === null || raw === undefined || raw === '') return undefined;
  if (raw === '1' || raw === 'true') return true;
  if (raw === '0' || raw === 'false') return false;
  return undefined;
}

/**
 * 从 localStorage 加载播放器设置；fadeInOut 优先从 cookie 读取。
 */
export function loadPlayerSettings() {
  try {
    const savedSettings = localStorage.getItem('musicPlayerSettings');
    if (savedSettings) {
      const parsed = JSON.parse(savedSettings);
      const fadeInOutCookie = getFadeInOutFromCookie();
      if (fadeInOutCookie !== undefined) {
        return { ...parsed, fadeInOut: fadeInOutCookie };
      }
      return parsed;
    }
  } catch (e) {
    console.warn('Failed to load player settings:', e);
  }
  return null;
}

/**
 * 需要持久化的设置 key 列表（与 save 入参字段一致，即 buildSettingsFromState 的返回值键名）。
 * 新增持久化字段时只需在此补充并在 buildSettingsFromState 中做映射。
 */
export const PLAYER_SETTINGS_KEYS = [
  'volume', 'muted', 'playMode', 'sleepTimerMinutes', 'fadeInOut', 'useWasmOpusDecode',
  'stabilityMode',
  'surroundEnabled', 'surroundIntensity', 'distortionEnabled', 'distortionIntensity',
  'bassLevel', 'reverbLevel', 'environmentMode', 'audioEnginePreset',
  'spaceModeEnabled', 'bassEnabled',
  'lyricSettings', 'equalizerPreset', 'lyricOffset',
  'playlistLoop', 'abLoopEnabled', 'abLoopStart', 'abLoopEnd',
  'audioFocusMode'
];

/**
 * buildSettingsFromState 入参对象的 state 键名列表（组件侧使用的名字）。
 * 与 PLAYER_SETTINGS_KEYS 的对应关系一一对应。
 * 主文件持久化 useEffect 的依赖数组应与本列表一致（便于后续用 useReducer 聚合时只改此处）。
 */
export const BUILD_INPUT_KEYS = [
  'volume', 'muted', 'playMode', 'sleepTimerMinutes', 'fadeInOut', 'useWasmOpusDecode',
  'stabilityMode',
  'surroundEnabled', 'surroundIntensity', 'distortionEnabled', 'distortionIntensity',
  'bassLevel', 'reverbLevel', 'environmentMode', 'audioEnginePreset',
  'spaceModeEnabled', 'bassEnabled',
  'lyricSettings', 'equalizerPreset', 'lyricOffset',
  'playlistLoop', 'abLoopEnabled', 'abLoopStart', 'abLoopEnd',
  'audioFocusMode'
];

/**
 * 从组件 state 快照构建持久化设置对象。
 * @param {Object} state - 包含 volume, muted 等组件 state
 * @returns {Object} 供 savePlayerSettings 使用的对象
 */
export function buildSettingsFromState(state) {
  if (!state) return {};
  return {
    volume: state.volume,
    muted: state.muted,
    playMode: state.playMode,
    sleepTimerMinutes: state.sleepTimerMinutes,
    fadeInOut: state.fadeInOut,
    useWasmOpusDecode: state.useWasmOpusDecode,
    stabilityMode: state.stabilityMode,
    surroundEnabled: state.surroundEnabled,
    surroundIntensity: state.surroundIntensity,
    distortionEnabled: state.distortionEnabled,
    distortionIntensity: state.distortionIntensity,
    bassLevel: state.bassLevel,
    reverbLevel: state.reverbLevel,
    environmentMode: state.environmentMode,
    audioEnginePreset: state.audioEnginePreset,
    spaceModeEnabled: state.spaceModeEnabled,
    bassEnabled: state.bassEnabled,
    lyricSettings: state.lyricSettings,
    equalizerPreset: state.equalizerPreset,
    lyricOffset: state.lyricOffset,
    playlistLoop: state.playlistLoop,
    abLoopEnabled: state.abLoopEnabled,
    abLoopStart: state.abLoopStart,
    abLoopEnd: state.abLoopEnd,
    audioFocusMode: state.audioFocusMode
  };
}

/**
 * 保存播放器设置到 localStorage；fadeInOut 同时写入 cookie。
 */
export function savePlayerSettings(settings) {
  try {
    localStorage.setItem('musicPlayerSettings', JSON.stringify(settings));
    if (typeof settings?.fadeInOut === 'boolean') {
      setCookieValue(FADE_IN_OUT_COOKIE, settings.fadeInOut ? '1' : '0');
    }
  } catch (e) {
    console.warn('Failed to save player settings:', e);
  }
}
